import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import useSWR, { mutate as globalMutate } from 'swr'
import { Archive, ArchiveRestore, Trash2 } from 'lucide-react'
import { Button } from '@ludus/ui'
import type { Pipeline, PipelineId } from '@ludus/core'
import { fetchRemovalReport, setPipelineActive } from '@/lib/api/pipelines.ts'
import { DeletePipelineDialog } from './DeletePipelineDialog.tsx'
import { blockerLabel } from './blockers.ts'

interface RemovalSectionProps {
  pipeline: Pipeline
}

/**
 * The two ways a board leaves the catalogue, side by side, because they are not
 * degrees of the same act: archiving hides a board and keeps every row, while
 * deletion is for the board that never held a card — a test, or a typo.
 *
 * Which one is offered is the engine's answer, never this component's guess:
 * `GET /removal` reports what the board holds and what forbids erasing it, and
 * the button that cannot be pressed says why rather than disappearing.
 */
export function RemovalSection({ pipeline }: RemovalSectionProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [busy, setBusy] = useState(false)

  const { data: report } = useSWR(`/pipelines/${pipeline.id}/removal`, () =>
    fetchRemovalReport(pipeline.id as PipelineId),
  )

  const toggleArchived = async () => {
    if (busy) return
    setBusy(true)
    try {
      await setPipelineActive(pipeline.id as PipelineId, !pipeline.isActive)
      await globalMutate('/pipelines')
      await globalMutate(`/pipelines/${pipeline.id}`)
      if (pipeline.isActive) navigate('/pipelines')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-border/50 bg-background-secondary/40 p-3.5">
      <header className="flex flex-col gap-0.5">
        <h2 className="text-sm font-semibold text-foreground">
          {t('pages.pipelines.removal.title', { defaultValue: 'Archive or delete' })}
        </h2>
        <p className="text-xs text-muted">
          {pipeline.isActive
            ? t('pages.pipelines.removal.subtitle', {
                defaultValue:
                  'Archiving takes the board out of the catalogue and keeps everything on it.',
              })
            : t('pages.pipelines.removal.archivedSubtitle', {
                defaultValue:
                  'This board is archived. It keeps every card and stage, and is not synchronised.',
              })}
        </p>
      </header>

      {report != null && (
        <p className="text-xs text-muted">
          {t('pages.pipelines.removal.holdings', {
            cards: report.holdings.cards,
            stages: report.holdings.stages,
            defaultValue: 'It holds {{cards}} card(s) and {{stages}} stage(s).',
          })}
        </p>
      )}

      {report != null && !report.canDelete && (
        <ul className="flex list-disc flex-col gap-1 pl-4 text-xs text-muted">
          {report.blockers.map((blocker) => (
            <li key={blocker.kind}>{blockerLabel(t, blocker)}</li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="secondary" size="sm" onClick={toggleArchived} disabled={busy}>
          {pipeline.isActive ? (
            <Archive className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <ArchiveRestore className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          {pipeline.isActive
            ? t('pages.pipelines.removal.archive', { defaultValue: 'Archive' })
            : t('pages.pipelines.removal.restore', { defaultValue: 'Restore' })}
        </Button>

        <Button
          variant="ghost"
          size="sm"
          disabled={report == null || !report.canDelete || busy}
          onClick={() => setConfirmingDelete(true)}
          className="text-danger hover:text-danger"
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
          {t('pages.pipelines.removal.delete', { defaultValue: 'Delete permanently' })}
        </Button>
      </div>

      {confirmingDelete && (
        <DeletePipelineDialog
          open={confirmingDelete}
          onOpenChange={setConfirmingDelete}
          pipeline={pipeline}
          onDeleted={async () => {
            await globalMutate('/pipelines')
            navigate('/pipelines')
          }}
        />
      )}
    </section>
  )
}
